import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { INDUSTRY_USE_CASES, WIDGET_TYPE_BADGE, WIDGET_TYPE_LABEL } from "../../data/industryUseCases";
import { DOC_SECTIONS, TIER_META, docPath } from "../../content/docs/docsManifest";

/**
 * Small inline helper: render an internal nav link as `<Link to="…">` so
 * SPA navigation skips the full document reload, but fall back to plain
 * `<a>` for pure hash anchors and any external URL. Keeps every call-site
 * one-liner-clean without sprinkling ternaries across the file.
 */
const isInternalNavHref = (href: string) =>
    href.startsWith("/") && !href.startsWith("//") && !/^https?:/i.test(href);

const NavAnchor = ({
    href,
    className,
    children,
    onClick,
    ...rest
}: {
    href: string;
    className?: string;
    children: React.ReactNode;
    onClick?: () => void;
} & Omit<React.HTMLAttributes<HTMLAnchorElement>, "href" | "className" | "children" | "onClick">) => {
    if (isInternalNavHref(href)) {
        return (
            <Link to={href} className={className} onClick={onClick} {...rest}>
                {children}
            </Link>
        );
    }
    return (
        <a href={href} className={className} onClick={onClick} {...rest}>
            {children}
        </a>
    );
};

const MenuIcon = () => (
    <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
    </svg>
);

const XIcon = () => (
    <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
    </svg>
);

const ChevronDownIcon = ({ className = "" }: { className?: string }) => (
    <svg className={`h-4 w-4 ${className}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
    </svg>
);

const ArrowRightIcon = ({ className = "" }: { className?: string }) => (
    <svg className={`h-4 w-4 ${className}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.5 4.5 21 12m0 0-7.5 7.5M21 12H3" />
    </svg>
);

type NavLeaf = { name: string; href: string; description?: string };
/** One labelled column inside a dropdown. A single unlabelled section renders as a plain list. */
type NavSection = { label?: string; items: NavLeaf[] };
/**
 * NavMenu describes a dropdown. One section renders single-column at 320px;
 * several render side by side as a mega panel. `industries` opts into the one
 * bespoke layout — the Use Cases grid of all 13 vertical packs.
 *
 * `align` matters because the wide panels are drawn by the outermost items:
 * centring a 44rem panel under the leftmost button pushes it off-viewport at
 * the lg breakpoint.
 */
type NavMenu = {
    name: string;
    sections: NavSection[];
    industries?: boolean;
    align?: "left" | "center" | "right";
};
type NavItem = NavLeaf | NavMenu;

const isMenu = (item: NavItem): item is NavMenu => "sections" in item;
const menuLeaves = (menu: NavMenu): NavLeaf[] => menu.sections.flatMap((section) => section.items);

// Build the industry subitems straight from the canonical data file so the
// dropdown stays in sync with the per-industry pages and the gallery.
const useCaseIndustryLeaves: NavLeaf[] = INDUSTRY_USE_CASES.map((ind) => ({
    name: ind.shortLabel,
    href: `/use-cases/${ind.slug}`,
    description: ind.tagline,
}));

// Both doc tiers deep-link to the first section rather than the hub, so the
// dropdown lands the reader inside the guide instead of on another index.
const docsTierLeaves: NavLeaf[] = (["quickstart", "manual"] as const).map((tier) => ({
    name: TIER_META[tier].label,
    href: docPath(tier, DOC_SECTIONS[0].slug),
    description: TIER_META[tier].tagline,
}));

const navigation: NavItem[] = [
    {
        name: "Product",
        align: "left",
        sections: [
            {
                label: "Chat",
                items: [
                    { name: "Chat Widget", href: "/ai-chat-widget", description: "Embed an AI chat widget on any website" },
                    { name: "Knowledge Base", href: "/ai-knowledge-base", description: "Crawl docs, PDFs and FAQs into searchable AI memory" },
                    { name: "Workflow Builder", href: "/ai-workflow-builder", description: "Design conversation flows visually" },
                ],
            },
            {
                label: "Voice",
                items: [
                    { name: "Voice Agents", href: "/ai-voice-agents", description: "AI voice agent for inbound + outbound calls" },
                    { name: "Call Pipeline", href: "/ai-call-pipeline", description: "Visual call-flow editor with vertical packs" },
                    { name: "Outbound Triggers", href: "/ai-outbound-triggers", description: "Webhooks, Shopify events, time-relative & more" },
                ],
            },
            {
                label: "Operations",
                items: [
                    { name: "CRM", href: "/ai-crm", description: "Tickets, contacts and pipeline, built in" },
                    { name: "Managed Support", href: "/managed-support", description: "Human agents and supervisors staffed by us" },
                ],
            },
        ],
    },
    {
        name: "Use Cases",
        industries: true,
        sections: [
            {
                items: [
                    { name: "Browse all use cases", href: "/use-cases", description: "Gallery of every vertical pack" },
                    { name: "Shopify AI Support", href: "/shopify-ai-customer-support", description: "Order, refund and tracking 24/7" },
                    ...useCaseIndustryLeaves,
                ],
            },
        ],
    },
    { name: "Pricing", href: "/pricing" },
    {
        name: "Resources",
        align: "right",
        sections: [
            {
                label: "Documentation",
                items: [
                    { name: "All documentation", href: "/docs", description: "Guides and the full platform reference" },
                    ...docsTierLeaves,
                    { name: "Shopify Integration", href: "/docs/shopify", description: "Scopes, webhooks and protected customer data" },
                ],
            },
            {
                label: "Learn",
                items: [
                    { name: "Blog", href: "/blogs", description: "Product news and playbooks" },
                    { name: "Case Studies", href: "/case-studies", description: "Real FloGPT deployments — OutreachBox, Pulse Real, InclusiveSpeak" },
                    { name: "How it Works", href: "/#how-it-works", description: "Architecture, infra and one-line install" },
                    { name: "Contact", href: "/contact", description: "Talk to a human" },
                ],
            },
        ],
    },
];

/** Render a leaf inside a dropdown (desktop). Compact variant for mega menu. */
const DropdownLeaf = ({ leaf, compact = false }: { leaf: NavLeaf; compact?: boolean }) => (
    <NavAnchor
        href={leaf.href}
        className="block rounded-xl px-3 py-2.5 hover:bg-slate-800/80 transition-colors"
    >
        <div className="flex items-start gap-2.5">
            <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-gradient-to-r from-orange-500 to-pink-500" />
            <div className="min-w-0">
                <div className="text-sm font-semibold text-white truncate">{leaf.name}</div>
                {leaf.description && !compact && (
                    <div className="text-xs text-slate-400 mt-0.5 leading-relaxed">{leaf.description}</div>
                )}
                {leaf.description && compact && (
                    <div className="text-[11px] text-slate-500 mt-0.5 leading-snug line-clamp-1">
                        {leaf.description}
                    </div>
                )}
            </div>
        </div>
    </NavAnchor>
);

/** Multi-column dropdown panel: one column per labelled section. */
const SectionsPanel = ({ sections }: { sections: NavSection[] }) => (
    <div className="rounded-2xl border border-slate-800 bg-slate-900/95 backdrop-blur-xl shadow-2xl shadow-black/40 p-4">
        <div className="flex gap-2">
            {sections.map((section) => (
                <div key={section.label} className="w-60 xl:w-64">
                    <div className="px-3 pb-2 text-[10px] font-semibold uppercase tracking-wider text-slate-500">
                        {section.label}
                    </div>
                    {section.items.map((leaf) => (
                        <DropdownLeaf key={leaf.href} leaf={leaf} />
                    ))}
                </div>
            ))}
        </div>
    </div>
);

/**
 * Mega menu for the Use Cases dropdown. Featured CTAs (Browse all,
 * Shopify) sit in their own column on the left, with the 13 industries
 * in a 2-column grid on the right.
 */
const UseCasesMegaPanel = ({ children }: { children: NavLeaf[] }) => {
    const [browseAll, shopify, ...industries] = children;

    return (
        <div className="w-[40rem] xl:w-[44rem] rounded-2xl border border-slate-800 bg-slate-900/95 backdrop-blur-xl shadow-2xl shadow-black/40 p-4">
            <div className="grid grid-cols-12 gap-3">
                {/* Featured column */}
                <div className="col-span-12 sm:col-span-4 flex flex-col gap-2">
                    <NavAnchor
                        href={browseAll.href}
                        className="group relative block rounded-xl border border-orange-500/30 bg-gradient-to-br from-orange-500/15 via-pink-500/10 to-slate-900 p-4 hover:border-orange-500/50 transition-colors overflow-hidden"
                    >
                        <div className="text-[10px] font-semibold uppercase tracking-wider text-orange-400">
                            Featured
                        </div>
                        <div className="mt-1 text-sm font-bold text-white">{browseAll.name}</div>
                        <div className="text-[11px] text-slate-400 mt-1 leading-relaxed">
                            {browseAll.description}
                        </div>
                        <div className="mt-3 inline-flex items-center gap-1 text-[11px] font-semibold text-orange-400">
                            View all {INDUSTRY_USE_CASES.length}
                            <ArrowRightIcon className="h-3 w-3 transition-transform group-hover:translate-x-1" />
                        </div>
                    </NavAnchor>
                    <NavAnchor
                        href={shopify.href}
                        className="group relative block rounded-xl border border-emerald-500/30 bg-gradient-to-br from-emerald-500/15 to-slate-900 p-4 hover:border-emerald-500/50 transition-colors"
                    >
                        <div className="text-[10px] font-semibold uppercase tracking-wider text-emerald-400">
                            Featured · Shopify
                        </div>
                        <div className="mt-1 text-sm font-bold text-white">{shopify.name}</div>
                        <div className="text-[11px] text-slate-400 mt-1 leading-relaxed">
                            {shopify.description}
                        </div>
                    </NavAnchor>
                </div>

                {/* Industries grid */}
                <div className="col-span-12 sm:col-span-8">
                    <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-500 px-2 mb-2">
                        By industry · Voice & Chat ({industries.length})
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-1 max-h-[20rem] overflow-y-auto pr-1">
                        {industries.map((leaf) => {
                            const ind = INDUSTRY_USE_CASES.find((i) => leaf.href === `/use-cases/${i.slug}`);
                            const Icon = ind?.Icon;
                            return (
                                <NavAnchor
                                    key={leaf.href}
                                    href={leaf.href}
                                    className="group flex items-start gap-2.5 rounded-lg px-2.5 py-2 hover:bg-slate-800/80 transition-colors"
                                >
                                    {Icon && ind && (
                                        <div className={`h-7 w-7 shrink-0 rounded-md bg-gradient-to-br ${ind.accent} flex items-center justify-center shadow`}>
                                            <Icon className="h-3.5 w-3.5 text-white" />
                                        </div>
                                    )}
                                    <div className="min-w-0">
                                        <div className="text-[12px] font-semibold text-white group-hover:text-orange-400 transition-colors truncate">
                                            {leaf.name}
                                        </div>
                                        {ind && (
                                            <div
                                                className={`text-[10px] font-semibold bg-gradient-to-r ${
                                                    WIDGET_TYPE_BADGE[ind.callWidgetType]
                                                } bg-clip-text text-transparent`}
                                            >
                                                {WIDGET_TYPE_LABEL[ind.callWidgetType]}
                                            </div>
                                        )}
                                    </div>
                                </NavAnchor>
                            );
                        })}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default function LandingNavbar() {
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const [scrolled, setScrolled] = useState(false);
    const [mobileOpenSection, setMobileOpenSection] = useState<string | null>(null);

    useEffect(() => {
        const handleScroll = () => setScrolled(window.scrollY > 20);
        window.addEventListener("scroll", handleScroll);
        return () => window.removeEventListener("scroll", handleScroll);
    }, []);

    useEffect(() => {
        const originalOverflow = document.body.style.overflow;
        document.body.style.overflow = mobileMenuOpen ? "hidden" : originalOverflow || "";
        return () => { document.body.style.overflow = originalOverflow || ""; };
    }, [mobileMenuOpen]);

    return (
        <>
            <header
                className={`fixed inset-x-0 top-0 z-50 transition-all duration-500 ${
                    scrolled ? "bg-slate-900/95 backdrop-blur-xl border-b border-slate-800" : "bg-transparent"
                }`}
            >
                <nav className="mx-auto max-w-7xl px-6 lg:px-8" aria-label="Global">
                    <div className="flex items-center justify-between py-6">
                        <div className="flex lg:flex-1">
                            <Link to="/" className="p-1.5 group">
                                <div className="flex items-center space-x-3">
                                    <div className="w-8 h-8 rounded-xl flex items-center justify-center group-hover:scale-105 transition-transform overflow-hidden">
                                        <img
                                            src="/images/flogpt-iso.png"
                                            alt="FloGPT"
                                            width={48}
                                            height={48}
                                            decoding="async"
                                            className="h-full w-full object-contain"
                                        />
                                    </div>
                                    <span className="text-xl font-bold text-white">FloGPT</span>
                                </div>
                            </Link>
                        </div>

                        <div className="flex lg:hidden">
                            <button
                                type="button"
                                className="inline-flex items-center justify-center rounded-lg p-2.5 text-slate-400 hover:bg-slate-800 transition-colors"
                                aria-label="Open menu"
                                onClick={() => setMobileMenuOpen(true)}
                            >
                                <MenuIcon />
                            </button>
                        </div>

                        <div className="hidden lg:flex lg:items-center lg:gap-x-8">
                            {navigation.map((item) =>
                                isMenu(item) ? (
                                    <div key={item.name} className="relative group">
                                        <button
                                            type="button"
                                            className="inline-flex items-center gap-1 text-sm font-medium text-slate-300 hover:text-orange-400 transition-colors focus:outline-none"
                                            aria-haspopup="true"
                                        >
                                            <span className="relative">
                                                {item.name}
                                                <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-gradient-to-r from-orange-500 to-pink-500 group-hover:w-full transition-all duration-300" />
                                            </span>
                                            <ChevronDownIcon className="transition-transform group-hover:rotate-180" />
                                        </button>
                                        {/* Hover/focus dropdown panel. The wrapper has pt-3 so the cursor
                                            can travel from the button into the panel without losing hover. */}
                                        <div
                                            className={`absolute top-full z-50 pt-3 invisible opacity-0 translate-y-1 group-hover:visible group-hover:opacity-100 group-hover:translate-y-0 group-focus-within:visible group-focus-within:opacity-100 group-focus-within:translate-y-0 transition-all duration-200 ${
                                                item.align === "left"
                                                    ? "left-0"
                                                    : item.align === "right"
                                                      ? "right-0"
                                                      : "left-1/2 -translate-x-1/2"
                                            }`}
                                        >
                                            {item.industries ? (
                                                <UseCasesMegaPanel children={menuLeaves(item)} />
                                            ) : item.sections.length > 1 ? (
                                                <SectionsPanel sections={item.sections} />
                                            ) : (
                                                <div className="w-80 rounded-2xl border border-slate-800 bg-slate-900/95 backdrop-blur-xl shadow-2xl shadow-black/40 p-2">
                                                    {menuLeaves(item).map((c) => (
                                                        <DropdownLeaf key={c.name} leaf={c} />
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                ) : (
                                    <NavAnchor
                                        key={item.name}
                                        href={item.href}
                                        className="text-sm font-medium text-slate-300 hover:text-orange-400 transition-colors relative group"
                                    >
                                        {item.name}
                                        <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-gradient-to-r from-orange-500 to-pink-500 group-hover:w-full transition-all duration-300" />
                                    </NavAnchor>
                                )
                            )}
                        </div>

                        <div className="hidden lg:flex lg:flex-1 lg:justify-end lg:gap-x-4">
                            <Link
                                to="/login"
                                className="text-sm font-medium text-slate-300 hover:text-orange-400 transition-colors px-4 py-2"
                            >
                                Sign in
                            </Link>
                            <Link
                                to="/signup"
                                className="group relative overflow-hidden rounded-xl bg-gradient-to-r from-orange-500 to-pink-500 px-6 py-2.5 text-sm font-semibold text-white shadow-lg transition-all hover:shadow-xl hover:scale-105"
                            >
                                <span className="relative z-10">Get Started</span>
                                <div className="absolute inset-0 bg-gradient-to-r from-orange-600 to-pink-600 opacity-0 group-hover:opacity-100 transition-opacity" />
                            </Link>
                        </div>
                    </div>
                </nav>
            </header>

            {/* Mobile menu */}
            {mobileMenuOpen && (
                <div className="lg:hidden">
                    <div className="fixed inset-0 z-[100] bg-slate-900/80" onClick={() => setMobileMenuOpen(false)} />
                    <div
                        className="fixed inset-y-0 right-0 z-[110] w-full max-w-sm bg-slate-900 shadow-2xl border-l border-slate-800 overflow-y-auto"
                        style={{ transform: "translateZ(0)" }}
                    >
                        <div className="p-6">
                            <div className="flex items-center justify-between">
                                <div className="flex items-center space-x-3">
                                    <div className="w-7 h-7 rounded-lg flex items-center justify-center overflow-hidden">
                                        <img
                                            src="/images/flogpt-iso.png"
                                            alt="FloGPT"
                                            width={48}
                                            height={48}
                                            decoding="async"
                                            className="h-full w-full object-contain"
                                        />
                                    </div>
                                    <span className="text-lg font-bold text-white">FloGPT</span>
                                </div>
                                <button
                                    type="button"
                                    className="rounded-lg p-2 text-slate-400 hover:bg-slate-800"
                                    aria-label="Close menu"
                                    onClick={() => setMobileMenuOpen(false)}
                                >
                                    <XIcon />
                                </button>
                            </div>
                            <div className="mt-8 space-y-1">
                                {navigation.map((item) =>
                                    isMenu(item) ? (
                                        <div key={item.name}>
                                            <button
                                                type="button"
                                                className="flex w-full items-center justify-between rounded-lg px-4 py-3 text-base font-semibold text-slate-200 hover:bg-slate-800 transition-colors"
                                                onClick={() =>
                                                    setMobileOpenSection((cur) => (cur === item.name ? null : item.name))
                                                }
                                                aria-expanded={mobileOpenSection === item.name}
                                            >
                                                <span>{item.name}</span>
                                                <ChevronDownIcon
                                                    className={`transition-transform ${
                                                        mobileOpenSection === item.name
                                                            ? "rotate-180 text-orange-400"
                                                            : "text-slate-500"
                                                    }`}
                                                />
                                            </button>
                                            {mobileOpenSection === item.name && (
                                                <div className="ml-3 mt-1 mb-2 space-y-1 border-l border-slate-800 pl-3">
                                                    {item.sections.map((section, index) => (
                                                        <div key={section.label ?? index}>
                                                            {section.label && (
                                                                <div className="px-3 pt-2 pb-1 text-[10px] font-semibold uppercase tracking-wider text-slate-500">
                                                                    {section.label}
                                                                </div>
                                                            )}
                                                            {section.items.map((c) => (
                                                                <NavAnchor
                                                                    key={c.name}
                                                                    href={c.href}
                                                                    className="block rounded-lg px-3 py-2.5 text-sm font-medium text-slate-300 hover:bg-slate-800 hover:text-white transition-colors"
                                                                    onClick={() => setMobileMenuOpen(false)}
                                                                >
                                                                    <div>{c.name}</div>
                                                                    {c.description && (
                                                                        <div className="text-xs text-slate-500 mt-0.5">
                                                                            {c.description}
                                                                        </div>
                                                                    )}
                                                                </NavAnchor>
                                                            ))}
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    ) : (
                                        <NavAnchor
                                            key={item.name}
                                            href={item.href}
                                            className="block rounded-lg px-4 py-3 text-base font-medium text-slate-200 hover:bg-slate-800 transition-colors"
                                            onClick={() => setMobileMenuOpen(false)}
                                        >
                                            {item.name}
                                        </NavAnchor>
                                    )
                                )}
                                <div className="pt-4 mt-4 border-t border-slate-800">
                                    <Link
                                        to="/login"
                                        className="block rounded-lg px-4 py-3 text-base font-medium text-slate-300 hover:bg-slate-800"
                                        onClick={() => setMobileMenuOpen(false)}
                                    >
                                        Sign in
                                    </Link>
                                    <Link
                                        to="/signup"
                                        className="mt-2 block rounded-xl bg-gradient-to-r from-orange-500 to-pink-500 px-4 py-3 text-center text-base font-semibold text-white shadow-lg"
                                        onClick={() => setMobileMenuOpen(false)}
                                    >
                                        Get Started
                                    </Link>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}
